import { lowerFirst } from '@sonar-software/sonar-ts-lib';
import { PardotRuleReference } from './PardotRuleReference';
import { PardotRuleReferenceSearch } from './PardotRuleReferenceSearch';
import { PeriscopeV3PaginatedList } from '..';
import { SearchFilterQueryParams } from '../filters';
import { keyPaginationDefaultQueryParams, keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import {
	KeyPaginationQueryParams,
	PaginatedResource,
	PeriscopePardotRule,
	SonarObjectType
} from '@sonar-software/sonar-ts-types';
import React, { useCallback } from 'react';
import { usePeriscopeActions, useQueryResource } from '@hooks';

export interface PardotRuleReferenceListProps {
	parentIndex: number;
	uri: string;
}

type Resource = PaginatedResource<PeriscopePardotRule>;
export type PardotRuleReferenceListQueryParams = KeyPaginationQueryParams & SearchFilterQueryParams;

export const PardotRuleReferenceList: React.FC<PardotRuleReferenceListProps> = ({ parentIndex, uri }) => {
	const { openPeriscope } = usePeriscopeActions();

	const { loading, queryParams, resource, updateParams } = useQueryResource<
		Resource,
		PardotRuleReferenceListQueryParams
	>({
		defaultParams: keyPaginationDefaultQueryParams,
		fallbackParams: keyPaginationFallbackQueryParams,
		uri
	});

	const renderRule = useCallback(
		(rule: PeriscopePardotRule, index: number) => {
			const handleRuleClick = (clickedField: PeriscopePardotRule) => {
				const { displayName, sonarObjectId, sonarObjectType } = clickedField;

				const ruleReferenceType = lowerFirst(sonarObjectType) as SonarObjectType;

				if (sonarObjectType) {
					openPeriscope({
						id: sonarObjectId,
						name: displayName,
						openedFromIndex: parentIndex,
						type: ruleReferenceType
					});
				}
			};

			return (
				<PardotRuleReference
					entity={ rule }
					key={ `${rule.sonarObjectId}-${index}` }
					onRuleClick={ handleRuleClick } />
			);
		},
		[ openPeriscope, parentIndex ]
	);

	return (
		<>
			<PardotRuleReferenceSearch onChange={ updateParams } />
			<PeriscopeV3PaginatedList
				loading={ loading }
				onChange={ updateParams }
				queryParams={ queryParams }
				renderListItem={ renderRule }
				resource={ resource } />
		</>
	);
};
